import { useState, useMemo } from 'react';
import { FileText, FileSpreadsheet, Image as ImageIcon, BarChart2, File } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { DOCUMENT_CATEGORIES } from '../constants/appConstants';
import type { CurrentUser } from '../types/currentUser';
import type { ShowToast, FetchData } from '../types/appShared';

// ══════════════════════════════════════════════
// useDocumentsController — نفس فكرة useTasksController/useMaterialsController،
// لصفحة Documents (الفلتر/البحث، مودال الرفع، الحذف، والإحصائيات). documentsData/
// setDocumentsData اتسابوا في App.jsx (بيتحمّلوا من fetchData المركزية).
//
// ✅ filteredDocuments و documentStats كانوا بيتحسبوا من جديد كل render (زي
// filteredTasks قبل كده في TasksView) من غير أي memoization — دلوقتي بقوا
// useMemo حقيقي.
// ══════════════════════════════════════════════
export interface AppDocument {
    document_id: string;
    category: string;
    title: string;
    description?: string | null;
    tags?: string | null;
    file_name: string;
    file_url: string;
    file_size: number;
    uploaded_by: string;
    uploaded_at: string;
}

interface UploadForm {
    file: File | null;
    category: string;
    title: string;
    description: string;
    tags: string;
}

interface UseDocumentsControllerParams {
    currentProjectId: string | null;
    currentUser: CurrentUser | null;
    documentsData: AppDocument[];
    fetchData: FetchData;
    showToast: ShowToast;
}

export function useDocumentsController({ currentProjectId, currentUser, documentsData, fetchData, showToast }: UseDocumentsControllerParams) {
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [documentFilter, setDocumentFilter] = useState("All");
    const [documentSearchTerm, setDocumentSearchTerm] = useState("");
    const [uploadForm, setUploadForm] = useState<UploadForm>({
        file: null,
        category: "Contract",
        title: "",
        description: "",
        tags: ""
    });
    const [uploadingDocument, setUploadingDocument] = useState(false);

    const deleteDocument = async (documentId: string, fileUrl?: string | null) => {
        try {
            let filePath = null;

            if (fileUrl) {
                const match = fileUrl.split('/public/documents/');
                if (match.length > 1) {
                    filePath = decodeURIComponent(match[1]);
                }
            }

            const { error: dbError } = await supabase
                .from('documents')
                .delete()
                .eq('document_id', documentId)
                .eq('project_id', currentProjectId);

            if (dbError) throw dbError;

            if (filePath) {
                const { error: storageError } = await supabase.storage
                    .from('documents')
                    .remove([filePath]);

                if (storageError) {
                    console.warn('Storage delete warning:', storageError);
                }
            }

            showToast('✅ Document deleted successfully!');
            fetchData();

        } catch (err: any) {
            console.error('Delete failed:', err);
            showToast('❌ Failed to delete document. Please try again.');
        }
    };

    const uploadDocument = async () => {
        if (!uploadForm.file || !uploadForm.title) {
            showToast("Please provide file and title");
            return;
        }
        // ✅ تحقق صريح (مكنش موجود قبل التحويل لـ TS) — currentUser.name تحت
        // كان هيعمل crash فعلي لو currentUser null.
        if (!currentUser) {
            showToast("You must be logged in to upload a document");
            setUploadingDocument(false);
            return;
        }

        setUploadingDocument(true);

        try {
            // Upload file to Supabase Storage
            const fileName = `${Date.now()}-${uploadForm.file.name}`;
            const filePath = `${currentProjectId}/documents/${fileName}`;

            const { error: uploadError } = await supabase.storage
                .from('documents')
                .upload(filePath, uploadForm.file);

            if (uploadError) {
                console.error('Upload error:', uploadError);
                throw uploadError;
            }

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('documents')
                .getPublicUrl(filePath);

            // Insert document record
            const documentId = `DOC-${Date.now()}`;

            const { error } = await supabase
                .from('documents')
                .insert([
                    {
                        project_id: currentProjectId,
                        document_id: documentId,
                        title: uploadForm.title,
                        category: uploadForm.category,
                        description: uploadForm.description,
                        file_url: publicUrl,
                        file_name: uploadForm.file.name,
                        file_size: uploadForm.file.size,
                        tags: uploadForm.tags,
                        uploaded_by: currentUser.name,
                        uploaded_at: new Date().toISOString()
                    }
                ])
                .select();

            if (error) throw error;

            showToast("Document uploaded successfully!");
            setShowUploadModal(false);
            setUploadForm({
                file: null,
                category: "Contract",
                title: "",
                description: "",
                tags: ""
            });

            // Refresh documents
            fetchData();

        } catch (err: any) {
            console.error("Upload error:", err);
            showToast("❌ Failed to upload document. Please try again.");
        } finally {
            setUploadingDocument(false);
        }
    };

    // Filter documents
    const filteredDocuments = useMemo(() => documentsData.filter(doc => {
        const matchesCategory = documentFilter === "All" || doc.category === documentFilter;
        const matchesSearch = !documentSearchTerm ||
            doc.title?.toLowerCase().includes(documentSearchTerm.toLowerCase()) ||
            doc.description?.toLowerCase().includes(documentSearchTerm.toLowerCase()) ||
            doc.tags?.toLowerCase().includes(documentSearchTerm.toLowerCase());

        return matchesCategory && matchesSearch;
    }), [documentsData, documentFilter, documentSearchTerm]);

    // Get file icon based on type
    const getFileIcon = (fileName?: string | null) => {
        const ext = (fileName?.split('.').pop() || '').toLowerCase();

        if (['pdf'].includes(ext)) return <FileText className="w-8 h-8 text-red-500" />;
        if (['doc', 'docx'].includes(ext)) return <FileText className="w-8 h-8 text-blue-500" />;
        if (['xls', 'xlsx'].includes(ext)) return <FileSpreadsheet className="w-8 h-8 text-green-500" />;
        if (['jpg', 'jpeg', 'png', 'gif'].includes(ext)) return <ImageIcon className="w-8 h-8 text-purple-500" />;
        if (['dwg', 'dxf'].includes(ext)) return <BarChart2 className="w-8 h-8 text-yellow-500" />;

        return <File className="w-8 h-8 text-gray-500" />;
    };

    // Format file size
    const formatFileSize = (bytes?: number | null) => {
        if (!bytes) return "Unknown";
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    // Document stats
    const documentStats = useMemo(() => ({
        total: documentsData.length,
        byCategory: DOCUMENT_CATEGORIES.reduce((acc: Record<string, number>, cat: string) => {
            acc[cat] = documentsData.filter(d => d.category === cat).length;
            return acc;
        }, {} as Record<string, number>)
    }), [documentsData]);

    return {
        showUploadModal, setShowUploadModal, documentFilter, setDocumentFilter,
        documentSearchTerm, setDocumentSearchTerm, uploadForm, setUploadForm, uploadingDocument,
        deleteDocument, uploadDocument, filteredDocuments, getFileIcon, formatFileSize, documentStats,
    };
}

export default useDocumentsController;
